import { AnchorIcon } from "../../../shared/Icons";
import Header from "../../../shared/app-shell/Header";
import Sidebar from "../../../shared/app-shell/Sidebar";
import { themeAtom } from "../../../shared/atoms";
import LoadingIndicator from "../../../shared/components/LoadingIndicator";
import VesselCardCommentsPanel from "../components/VesselCardCommentsPanel";
import VesselCardCurrentPerformancePanel from "../components/VesselCardCurrentPerformancePanel";
import VesselCardDashboardHeader from "../components/VesselCardDashboardHeader";
import VesselCardDetailPanel from "../components/VesselCardDetailPanel";
import VesselCardEvaluationPanel from "../components/VesselCardEvaluationPanel";
import VesselCardModal from "../components/VesselCardModal";
import VesselCardPerformancePanel from "../components/VesselCardPerformancePanel";
import VesselCardSkeleton from "../components/VesselCardSkeleton";
import VesselCardTcoPanel from "../components/VesselCardTcoPanel";
import { useVesselCard } from "../hooks/useVesselCard";
import { Cog6ToothIcon, ShieldCheckIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { Navigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const foulingToneClassNames = {
  positive:
    "border-emerald-300 bg-emerald-100 text-emerald-900 hover:bg-emerald-200 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200",
  warning:
    "border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-200 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200",
  neutral:
    "border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200",
  negative:
    "border-red-300 bg-red-100 text-red-900 hover:bg-red-200 dark:border-red-800 dark:bg-red-950/50 dark:text-red-200",
};

// Opens the URL's vessel directly and keeps search available while its card is loading.
// Evaluation requests are saved through OpsLink; unrelated Power Automate email actions remain unavailable.
// Scrubber selection is read-only for OBS_Actual; Design selections also correct expected consumption for HSFO.
// Keeps the shell inside the viewport so only the card content scrolls vertically.
const VesselCardPage = () => {
  const theme = useAtomValue(themeAtom);
  const dashboard = useVesselCard();
  const { card } = dashboard;

  if (dashboard.redirectTo) {
    return <Navigate to={dashboard.redirectTo} replace />;
  }

  const cardHeader = (
    <VesselCardDashboardHeader
      vessel={card?.vessel ?? null}
      selectedImo={dashboard.selectedImo}
      onOpenHelp={() => dashboard.openModal("help")}
      vessels={dashboard.vessels}
      vesselQuery={dashboard.vesselQuery}
      vesselStatus={dashboard.vesselStatus}
      vesselError={dashboard.vesselError}
      hasMoreVessels={dashboard.hasMoreVessels}
      isLoadingMoreVessels={dashboard.isLoadingMoreVessels}
      onQueryChange={dashboard.setVesselQuery}
      onLoadMoreVessels={dashboard.loadMoreVessels}
      onSelectVessel={dashboard.selectVessel}
      onSearchOpenChange={dashboard.setSearchEnabled}
      useMockData={dashboard.useMockData}
    />
  );

  return (
    <div className="flex h-full min-h-0" data-test="vessel-card-page">
      <Sidebar />
      <div
        className={twMerge(
          "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden",
          theme.home.tailwind.pageBackground,
          theme.home.tailwind.textDefault,
        )}
      >
        <Header />
        <main
          data-test="vessel-card-content"
          className="mt-2 min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-10 pt-6 sm:px-6"
        >
          <div className="mx-auto max-w-[1800px]">
            {cardHeader}

            {!card && dashboard.selectedImo && dashboard.status === "loading" && (
              <p
                role="status"
                data-test="vessel-card-loading-status"
                className="mb-4 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"
              >
                <span aria-hidden="true" className="inline-flex text-green dark:text-teal-200">
                  <LoadingIndicator loadingIndicatorSize="xs" className="motion-reduce:animate-none" />
                </span>
                Loading vessel card…
              </p>
            )}
            {!card && dashboard.status !== "error" && <VesselCardSkeleton />}
            {dashboard.status === "error" && (
              <div
                role="alert"
                data-test="vessel-card-error"
                className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800"
              >
                {dashboard.error}
              </div>
            )}
            {dashboard.useMockData && (
              <div
                data-test="vessel-card-mock-banner"
                className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-900"
              >
                Showing Vessel Card demonstration data. Vessel details and CP inputs are synthetic examples.
              </div>
            )}

            {card && dashboard.response && dashboard.status === "success" && (
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                <VesselCardEvaluationPanel
                  performanceBasis={card.performanceBasis}
                  evaluatedAt={card.evaluatedAt}
                  lastFixtureAt={card.lastFixtureAt}
                  scrubberFitted={dashboard.scrubberFitted}
                  scrubberSelectionDisabled={dashboard.scrubberSelectionDisabled}
                  scrubberSelectionHelp={dashboard.scrubberSelectionHelp}
                  scrubberWarning={dashboard.scrubberWarning}
                  onSelectScrubber={dashboard.selectScrubber}
                  cpSource={dashboard.cpSource}
                  cpSourceMessage={dashboard.cpSourceMessage}
                  onSelectCpSource={dashboard.selectCpSource}
                  onOpenCustomCp={() => dashboard.openModal("customCp")}
                />

                <div className="xl:col-span-3">
                  <VesselCardDetailPanel
                    title="Vessel particulars"
                    icon={<AnchorIcon className="h-5 w-5 text-green" />}
                    details={card.vesselParticulars}
                    dataTest="vessel-card-particulars"
                  />
                </div>

                <div className="xl:col-span-7">
                  <VesselCardPerformancePanel
                    imo={card.vessel.imo}
                    rows={card.performance}
                    cpLabel={
                      dashboard.cpSource === "imos"
                        ? "IMOS CP Desc"
                        : dashboard.cpSource === "imosInput"
                          ? "IMOS Input"
                          : "Custom CP"
                    }
                    view={dashboard.view}
                    onViewChange={dashboard.setView}
                    comparison={dashboard.comparison}
                    onComparisonChange={dashboard.setComparison}
                    balticRows={dashboard.balticRows}
                    balticIndex={dashboard.balticIndex}
                    onBalticIndexChange={dashboard.setBalticIndex}
                    balticBenchmarkAvailable={dashboard.balticBenchmarkAvailable}
                    fuelGrade={dashboard.fuelGrade}
                  />
                </div>

                <VesselCardCurrentPerformancePanel performance={card.currentPerformance} />

                <div className="xl:col-span-3">
                  <VesselCardDetailPanel
                    title="Engine & design"
                    icon={<Cog6ToothIcon className="h-5 w-5 text-green" />}
                    details={card.engineAndDesign}
                    dataTest="vessel-card-engine-design"
                  />
                </div>

                <button
                  type="button"
                  data-test="vessel-card-request-evaluation-button"
                  onClick={() => dashboard.openModal("request")}
                  className="rounded-lg border border-green bg-primary-10 px-5 py-4 text-left text-sm font-semibold text-green shadow-sm transition-colors hover:bg-primary-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green dark:bg-teal-950/40 dark:text-teal-200 xl:col-span-9"
                >
                  Request performance evaluation
                </button>

                <button
                  type="button"
                  aria-haspopup="dialog"
                  title={card.foulingDetails ?? "Select to inspect the idle-stay details."}
                  data-test="vessel-card-fouling-factor-button"
                  onClick={() => dashboard.openModal("fouling")}
                  className={twMerge(
                    "rounded-lg border px-4 py-3 text-center shadow-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green xl:col-span-3",
                    foulingToneClassNames[card.foulingTone],
                  )}
                >
                  <span className="block text-xs leading-5">Fouling factor · select for details</span>
                  <strong className="mt-1 block text-lg">{card.foulingFactor ?? "Not available"}</strong>
                </button>

                <VesselCardTcoPanel rows={card.tcoDescriptions} onReceiveByEmail={() => dashboard.openModal("email")} />

                <VesselCardCommentsPanel comments={card.comments} onAddComment={() => dashboard.openModal("comment")} />

                <div className="space-y-4 xl:col-span-3">
                  <VesselCardDetailPanel
                    title="RightShip"
                    icon={<ShieldCheckIcon className="h-5 w-5 text-amber-500" />}
                    details={card.rightShip}
                    dataTest="vessel-card-rightship"
                  />
                  <VesselCardDetailPanel
                    title="Ownership"
                    icon={<UserGroupIcon className="h-5 w-5 text-green" />}
                    details={card.ownership}
                    dataTest="vessel-card-ownership"
                  />
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {card && dashboard.status === "success" && dashboard.modal && (
        <VesselCardModal
          modal={dashboard.modal}
          card={card}
          customCp={dashboard.customCp}
          onCustomCpChange={dashboard.setCustomCp}
          onComment={dashboard.addComment}
          onClose={dashboard.closeModal}
          useMockData={dashboard.useMockData}
        />
      )}
    </div>
  );
};

export default VesselCardPage;
